import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  query,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { db } from "../utils/firebase.js";
import moment from "moment";
import SignatureCanvas from "react-signature-canvas";
import { useAuth } from "../hooks/AuthenticationProvider";
import {
  Plus,
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Edit,
  FileText,
  X,
  Sun,
  Moon,
  AlertOctagon,
  Menu,
  Pill,
} from "lucide-react";
const MedicationLog = () => {
  const { residentId } = useParams();
  const navigate = useNavigate();
  const { organizationId, user, role } = useAuth();
  const [activeTab, setActiveTab] = useState("active");
  const [medications, setMedications] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [residentSignature, setResidentSignature] = useState(null);
  const [staffSignature, setStaffSignature] = useState(null);
  const [resident, setResident] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedMedicationLogs, setSelectedMedicationLogs] = useState([]);
  const [isLogDetailModalOpen, setIsLogDetailModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  // Form states
  const [formData, setFormData] = useState({
    name: "",
    dosage: "",
    totalPrescribed: 30,
    count: 0,
    controlled: false,
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().add(30, "days").format("YYYY-MM-DD"),
    prescriber: "",
    pharmacy: "",
    instructions: "",
    dosesPerDay: 1,
    unitsPerDose: 1,
    maxDaysSupply: 1,
    lastDisbursementDate: null,
    nextScheduledDisbursement: null,
    remainingDoses: 0,
    administrationTime: [],
    doseSchedules: [], // For medications with multiple dose times
    isPRN: false, // As needed medication
  });
  const [logData, setLogData] = useState({
    actualCount: 0,
    notes: "",
    reason: "verification",
    daysSupply: 1,
  });
  // Filter state
  const [dateFilter, setDateFilter] = useState("");
  useEffect(() => {
    const fetchResidentData = async () => {
      try {
        if (!organizationId || !residentId) return;
        // Fetch resident data
        const residentRef = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentId
        );
        const residentSnap = await getDoc(residentRef);
        if (residentSnap.exists()) {
          const residentData = { id: residentSnap.id, ...residentSnap.data() };
          setResident(residentData);
          // Convert medications from the resident data to the format needed for the UI
          if (
            residentData.medications &&
            Array.isArray(residentData.medications)
          ) {
            const formattedMeds = residentData.medications.map((med, index) => {
              // Calculate days until refill if dates are available
              let daysUntilRefill = 30; // Default value
              if (med.refillDate) {
                const refillDate =
                  typeof med.refillDate === "string"
                    ? new Date(med.refillDate)
                    : med.refillDate.toDate
                    ? med.refillDate.toDate()
                    : new Date();
                daysUntilRefill = Math.max(
                  0,
                  Math.floor((refillDate - new Date()) / (1000 * 60 * 60 * 24))
                );
              }
              return {
                id: med.id || `med-${index}`,
                name: med.name || "",
                dosage: med.dose || "",
                count: med.count || 0,
                totalPrescribed: med.totalPrescribed || 30,
                startDate:
                  med.startDate || new Date().toISOString().split("T")[0],
                endDate:
                  med.refillDate ||
                  new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split("T")[0],
                prescriber: med.prescriber || "",
                pharmacy: med.pharmacy || "",
                instructions: med.instructions || "",
                status: daysUntilRefill <= 0 ? "refill-needed" : "active",
                controlled: med.controlled || false,
                daysUntilRefill,
              };
            });
            setMedications(formattedMeds);
          }
        }
        // Fetch medication logs from the medication logs subcollection
        const logsRef = collection(
          db,
          `organizations/${organizationId}/residents/${residentId}/medicationLogs`
        );
        const logsQuery = query(logsRef, orderBy("date", "desc"));
        const logsSnapshot = await getDocs(logsQuery);
        if (!logsSnapshot.empty) {
          const logsData = logsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            date: doc.data().date?.toDate
              ? doc.data().date.toDate()
              : doc.data().date,
          }));
          setDailyLogs(logsData);
        }
      } catch (error) {
        console.error("Error fetching resident data:", error);
        showNotification(
          "Error",
          `Failed to fetch resident data: ${error.message}`,
          "error"
        );
      }
    };
    fetchResidentData();
  }, [organizationId, residentId]);
  const openEditModal = (medication) => {
    console.log("Opening edit modal for medication:", medication);
    // Format dates properly - ensure we always have strings in YYYY-MM-DD format
    const startDate = medication.startDate
      ? typeof medication.startDate === "string"
        ? medication.startDate
        : moment(medication.startDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    const endDate =
      medication.endDate || medication.refillDate
        ? typeof medication.endDate === "string"
          ? medication.endDate
          : typeof medication.refillDate === "string"
          ? medication.refillDate
          : moment(medication.endDate || medication.refillDate).format(
              "YYYY-MM-DD"
            )
        : moment().add(30, "days").format("YYYY-MM-DD");
    // Make sure administrationTime is always an array
    const administrationTime = Array.isArray(medication.administrationTime)
      ? medication.administrationTime
      : medication.administrationTime
      ? [medication.administrationTime]
      : [];
    setFormData({
      name: medication.name || "",
      dosage: medication.dosage || medication.dose || "",
      totalPrescribed: medication.totalPrescribed || 30,
      count: medication.count || 0,
      controlled: medication.controlled || false,
      startDate: startDate,
      endDate: endDate,
      prescriber: medication.prescriber || "",
      pharmacy: medication.pharmacy || "",
      instructions: medication.instructions || "",
      dosesPerDay: medication.dosesPerDay || 1,
      unitsPerDose: medication.unitsPerDose || 1,
      maxDaysSupply: medication.maxDaysSupply || 1,
      administrationTime: administrationTime,
      isPRN: medication.isPRN || false,
    });
    console.log("Form data set to:", {
      name: medication.name || "",
      dosage: medication.dosage || medication.dose || "",
      totalPrescribed: medication.totalPrescribed || 30,
      count: medication.count || 0,
      controlled: medication.controlled || false,
      startDate: startDate,
      endDate: endDate,
      // other fields omitted for brevity
    });
    setSelectedMedication(medication);
    setIsEditMode(true);
    setIsAddModalOpen(true);
  };
  const openDetailsModal = (medication) => {
    setSelectedMedication(medication);
    // Filter logs related to this specific medication
    const medicationLogs = dailyLogs.filter(
      (log) => log.medicationId === medication.id
    );
    setSelectedMedicationLogs(medicationLogs);
    setIsDetailsModalOpen(true);
  };
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  // Handle log form input changes
  const handleLogInputChange = (e) => {
    const { name, value } = e.target;
    setLogData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const openLogDetailModal = (log) => {
    setSelectedLog(log);
    setIsLogDetailModalOpen(true);
  };
  // Add this function after handleLogInputChange
  const calculateExpectedCountChange = (medication, daysSupply = 1) => {
    if (!medication) return 0;
    const dosesPerDay = parseFloat(medication.dosesPerDay) || 1;
    const unitsPerDose = parseFloat(medication.unitsPerDose) || 1;
    const days = parseInt(daysSupply) || 1;
    // Calculate total units that should be disbursed
    return Math.round(days * dosesPerDay * unitsPerDose);
  };
  const expectedCountAfterDisbursement = selectedMedication
    ? Math.max(
        0,
        selectedMedication.count -
          calculateExpectedCountChange(
            selectedMedication,
            parseInt(logData.daysSupply) || 1
          )
      )
    : 0;
  // Handle adding a new medication
  const handleAddMedication = async () => {
    try {
      const now = new Date();
      const refillDate = formData.endDate
        ? new Date(formData.endDate)
        : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const newMedication = {
        id: Date.now().toString(), // or selectedMedication.id for update
        name: formData.name || "",
        dose: formData.dosage || "",
        count: parseInt(formData.count) || 0,
        totalPrescribed: parseInt(formData.totalPrescribed) || 30,
        startDate: formData.startDate || moment().format("YYYY-MM-DD"),
        refillDate: moment(refillDate).format("YYYY-MM-DD"),
        prescriber: formData.prescriber || "",
        pharmacy: formData.pharmacy || "",
        instructions: formData.instructions || "",
        controlled: Boolean(formData.controlled),
        // Add these new fields:
        dosesPerDay: parseFloat(formData.dosesPerDay) || 1,
        unitsPerDose: parseFloat(formData.unitsPerDose) || 1,
        maxDaysSupply: parseInt(formData.maxDaysSupply) || 1,
        isPRN: Boolean(formData.isPRN),
        administrationTime: Array.isArray(formData.administrationTime)
          ? formData.administrationTime
          : [],
      };
      // Update the resident's medications array in Firestore
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );
      const residentDoc = await getDoc(residentRef);
      if (residentDoc.exists()) {
        const currentMeds = residentDoc.data().medications || [];
        await updateDoc(residentRef, {
          medications: [...currentMeds, newMedication],
        });
      }
      // Format for local state
      const formattedMed = {
        ...newMedication,
        dosage: newMedication.dose,
        status: "active",
        daysUntilRefill: moment(refillDate).diff(moment(), "days"),
      };
      setMedications([...medications, formattedMed]);
      setIsAddModalOpen(false);
      // Reset form
      setFormData({
        name: "",
        dosage: "",
        totalPrescribed: 30,
        count: 0,
        controlled: false,
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().add(30, "days").format("YYYY-MM-DD"),
        prescriber: "",
        pharmacy: "",
        instructions: "",
        dosesPerDay: 1,
        unitsPerDose: 1,
        maxDaysSupply: 1,
        lastDisbursementDate: null,
        nextScheduledDisbursement: null,
        remainingDoses: 0,
        administrationTime: [],
        isPRN: false,
      });
      showNotification(
        "Success",
        `${formData.name} has been added to the medication list.`,
        "success"
      );
    } catch (error) {
      console.error("Error adding medication:", error);
      showNotification(
        "Error",
        `Failed to add medication: ${error.message}`,
        "error"
      );
    }
  };
  // Handle updating an existing medication
  const handleUpdateMedication = async () => {
    try {
      const now = new Date();
      const refillDate = formData.endDate
        ? new Date(formData.endDate)
        : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      // Create updated medication object
      const updatedMedication = {
        id: selectedMedication.id, // Preserve the ID for matching
        name: formData.name || "",
        dose: formData.dosage || "",
        // Count should only be updated through the Log Check to maintain audit trail
        count: selectedMedication.count,
        totalPrescribed: parseInt(formData.totalPrescribed) || 30,
        startDate: formData.startDate || moment().format("YYYY-MM-DD"),
        refillDate: moment(refillDate).format("YYYY-MM-DD"),
        prescriber: formData.prescriber || "",
        pharmacy: formData.pharmacy || "",
        instructions: formData.instructions || "",
        controlled: Boolean(formData.controlled),
        dosesPerDay: parseFloat(formData.dosesPerDay) || 1,
        unitsPerDose: parseFloat(formData.unitsPerDose) || 1,
        maxDaysSupply: parseInt(formData.maxDaysSupply) || 1,
        isPRN: Boolean(formData.isPRN),
        administrationTime: Array.isArray(formData.administrationTime)
          ? formData.administrationTime
          : [],
      };
      // Log what we're trying to update for debugging
      console.log("Updating medication with ID:", selectedMedication.id);
      console.log("Updated medication data:", updatedMedication);
      // Update the resident's medications array in Firestore
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );
      // Get current resident document
      const residentDoc = await getDoc(residentRef);
      if (residentDoc.exists()) {
        // Get current medications array
        const currentMeds = residentDoc.data().medications || [];
        console.log("Current medications in DB:", currentMeds);
        // Find the index of the medication we want to update
        const medicationIndex = currentMeds.findIndex(
          (med) => med.id === selectedMedication.id
        );
        console.log("Found medication at index:", medicationIndex);
        if (medicationIndex !== -1) {
          // Create a new array with the updated medication
          const updatedMeds = [...currentMeds];
          updatedMeds[medicationIndex] = updatedMedication;
          // Update the document in Firestore
          await updateDoc(residentRef, {
            medications: updatedMeds,
          });
          console.log("Firestore update successful");
          // Format for local state
          const formattedMed = {
            ...updatedMedication,
            dosage: updatedMedication.dose,
            status: "active",
            daysUntilRefill: moment(refillDate).diff(moment(), "days"),
          };
          // Update the medications array in local state
          setMedications(
            medications.map((med) =>
              med.id === selectedMedication.id ? formattedMed : med
            )
          );
          setIsAddModalOpen(false);
          setIsEditMode(false);
          setSelectedMedication(null);
          // Reset form
          setFormData({
            name: "",
            dosage: "",
            totalPrescribed: 30,
            count: 0,
            controlled: false,
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().add(30, "days").format("YYYY-MM-DD"),
            prescriber: "",
            pharmacy: "",
            instructions: "",
            dosesPerDay: 1,
            unitsPerDose: 1,
            maxDaysSupply: 1,
            lastDisbursementDate: null,
            nextScheduledDisbursement: null,
            remainingDoses: 0,
            administrationTime: [],
            isPRN: false,
          });
          showNotification(
            "Success",
            `${formData.name} has been updated.`,
            "success"
          );
        } else {
          // Medication not found in array
          console.error(
            "Could not find medication with ID:",
            selectedMedication.id
          );
          showNotification(
            "Error",
            `Failed to update medication: Medication not found in database.`,
            "error"
          );
        }
      } else {
        console.error("Resident document not found");
        showNotification(
          "Error",
          "Failed to update medication: Resident not found in database.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating medication:", error);
      showNotification(
        "Error",
        `Failed to update medication: ${error.message}`,
        "error"
      );
    }
  };

  // Handle logging a medication check
  const handleLogMedication = async () => {
    try {
      const { actualCount, notes, reason, daysSupply } = logData;

      // Check if signatures are provided, if required
      if (
        selectedMedication.controlled &&
        (!residentSignature || !staffSignature)
      ) {
        showNotification(
          "Error",
          "Both resident and staff signatures are required for controlled substances",
          "error"
        );
        return;
      }
      // Prepare staff information
      const staffName = user?.displayName || user?.email || "Staff Member";
      const staffRole = role || "Staff";
      // Create log entry
      const newLog = {
        date: new Date(),
        medicationId: selectedMedication.id,
        medicationName: selectedMedication.name,
        dosesPerDay: selectedMedication.dosesPerDay,
        unitsPerDose: selectedMedication.unitsPerDose,
        expectedCount: selectedMedication.count,
        actualCount: parseInt(actualCount),
        reason,
        notes: notes || "",
        daysSupply:
          reason === "administration" ? parseInt(daysSupply) || 1 : null,
        expectedDecrease:
          reason === "administration"
            ? calculateExpectedCountChange(
                selectedMedication,
                parseInt(daysSupply) || 1
              )
            : null,
        residentSignature: residentSignature
          ?.getTrimmedCanvas()
          .toDataURL("image/png"),
        staffSignature: staffSignature
          ?.getTrimmedCanvas()
          .toDataURL("image/png"),
        residentName:
          resident?.fullName || `${resident?.firstName} ${resident?.lastName}`,
        staffName: staffName,
        staffEmail: user?.email || null,
        staffRole: role || "Staff",
        timeCompleted: moment().format("hh:mm A"),
      };
      // Additional tracking for administrations
      if (reason === "administration") {
        const daysSupplyValue = parseInt(daysSupply) || 1;
        // Set last disbursement date and next scheduled disbursement
        newLog.lastDisbursementDate = new Date();
        newLog.nextScheduledDisbursement = moment()
          .add(daysSupplyValue, "days")
          .toDate();
        // Calculate and store how many days of medication the resident should have
        const dailyUnits =
          (selectedMedication.dosesPerDay || 1) *
          (selectedMedication.unitsPerDose || 1);
        newLog.daysRemaining = Math.floor(parseInt(actualCount) / dailyUnits);
      }
      // Save the log to Firestore
      const logRef = await addDoc(
        collection(
          db,
          `organizations/${organizationId}/residents/${residentId}/medicationLogs`
        ),
        newLog
      );
      // Add the new log to the local state with the generated ID
      const logWithId = { ...newLog, id: logRef.id };
      setDailyLogs([logWithId, ...dailyLogs]);
      // Update the medication count and next disbursement info in Firestore
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );
      const residentDoc = await getDoc(residentRef);
      if (residentDoc.exists()) {
        const currentMeds = residentDoc.data().medications || [];
        const updatedMeds = currentMeds.map((med) => {
          if (med.id === selectedMedication.id) {
            return {
              ...med,
              count: parseInt(actualCount),
              lastDisbursementDate: newLog.lastDisbursementDate,
              nextScheduledDisbursement: newLog.nextScheduledDisbursement,
              daysRemaining: newLog.daysRemaining,
            };
          }
          return med;
        });
        await updateDoc(residentRef, {
          medications: updatedMeds,
        });
        // Update local state
        setMedications(
          medications.map((med) => {
            if (med.id === selectedMedication.id) {
              return {
                ...med,
                count: parseInt(actualCount),
                lastDisbursementDate: newLog.lastDisbursementDate,
                nextScheduledDisbursement: newLog.nextScheduledDisbursement,
                daysRemaining: newLog.daysRemaining,
              };
            }
            return med;
          })
        );
      }
      // Close the modal and reset state
      setIsLogModalOpen(false);
      setSelectedMedication(null);
      setLogData({
        actualCount: 0,
        notes: "",
        reason: "verification",
        daysSupply: 1,
      });
      clearSignatures();

      showNotification(
        "Success",
        `${selectedMedication.name} check has been logged successfully.`,
        "success"
      );
    } catch (error) {
      console.error("Error logging medication check:", error);
      showNotification(
        "Error",
        `Failed to log medication check: ${error.message}`,
        "error"
      );
    }
  };
  const getMedicationsForTimeOfDay = (timeOfDay) => {
    if (!medications || !Array.isArray(medications)) return [];
    return medications.filter((med) => {
      // Skip PRN medications
      if (med.isPRN) return false;
      // Check if administrationTime is an array (multi-select)
      if (Array.isArray(med.administrationTime)) {
        return med.administrationTime.includes(timeOfDay);
      }
      // Fallback for older entries that have a single string value
      return med.administrationTime === timeOfDay;
    });
  };
  // Function to get PRN (as needed) medications
  const getPRNMedications = () => {
    if (!medications || !Array.isArray(medications)) return [];
    return medications.filter((med) => med.isPRN);
  };
  // Function to generate and download a PDF of the medication schedule
  const generateMedicationSchedulePDF = () => {
    // In a real implementation, you would generate a PDF here
    // This is just a placeholder notification
    showNotification(
      "Info",
      "This would generate a printable medication schedule PDF",
      "info"
    );
  };
  const checkMedicationCompliance = () => {
    const date = moment(scheduleDate).format("YYYY-MM-DD");
    const todayLogs = dailyLogs.filter(
      (log) =>
        moment(log.date).format("YYYY-MM-DD") === date &&
        log.reason === "administration"
    );
    // Group logs by medication
    const logsByMedication = {};
    todayLogs.forEach((log) => {
      if (!logsByMedication[log.medicationId]) {
        logsByMedication[log.medicationId] = [];
      }
      logsByMedication[log.medicationId].push(log);
    });
    // Get all non-PRN medications
    const scheduledMeds = medications.filter((med) => !med.isPRN);
    // Track compliance
    const compliance = {
      total: scheduledMeds.length,
      administered: 0,
      missed: [],
    };
    scheduledMeds.forEach((med) => {
      const medLogs = logsByMedication[med.id] || [];
      if (medLogs.length > 0) {
        compliance.administered++;
      } else {
        compliance.missed.push(med);
      }
    });
    return compliance;
  };
  // Open the log modal for a specific medication
  const openLogModal = (medication) => {
    setSelectedMedication(medication);
    // Pre-fill with the correct reason if specified
    setLogData({
      actualCount: medication.count,
      notes: "",
      reason: medication.preFillReason || "verification",
      daysSupply: 1,
    });
    setIsLogModalOpen(true);
  };
  // Clear signature pads
  const clearSignatures = () => {
    residentSignature?.clear();
    staffSignature?.clear();
  };
  // Show notification
  const showNotification = (title, message, type = "info") => {
    // You can implement your own notification system here
    // For now, we'll just use console logs
    console.log(`[${type.toUpperCase()}] ${title}: ${message}`);
    // In a real implementation, you would display a toast or notification component
  };
  // Filter logs based on selected date
  const filteredLogs = dateFilter
    ? dailyLogs.filter((log) => {
        // Convert log date to string format for comparison
        const logDate = moment(log.date).format("YYYY-MM-DD");
        return logDate === dateFilter;
      })
    : dailyLogs;

  return (
    <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
      {/* Ambient background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Header section */}
        <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-500/10 rounded-xl">
                <Pill className="text-cyan-400 h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  Medication Log
                </h1>
                {resident && (
                  <p className="text-slate-400">
                    for {resident.firstName} {resident.lastName}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
            >
              <Plus size={20} />
              Add Medication
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 overflow-hidden">
          <div className="flex border-b border-slate-700">
            <button
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === "active"
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setActiveTab("active")}
            >
              <Pill size={18} />
              Active Medications
            </button>
            <button
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === "log"
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setActiveTab("log")}
            >
              <Calendar size={18} />
              Daily Log
            </button>
            <button
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === "schedule"
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setActiveTab("schedule")}
            >
              <Calendar size={18} />
              Medication Schedule
            </button>
            <button
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === "refill"
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setActiveTab("refill")}
            >
              <Clock size={18} />
              Refill Management
            </button>
          </div>

          {/* Active Medications Tab */}
          {activeTab === "active" && (
            <div className="p-6">
              {medications.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-slate-700">
                        <th className="pb-3 text-slate-300 font-medium">
                          Medication
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Schedule
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Count
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Refill
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Status
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {medications.map((med) => (
                        <tr
                          key={med.id}
                          className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors"
                        >
                          <td className="py-4">
                            <div>
                              <div className="text-white font-medium">
                                {med.name}
                              </div>
                              <div className="text-slate-400 text-sm">
                                {med.dosage}
                              </div>
                            </div>
                          </td>
                          <td className="py-4">
                            <span className="text-white">{med.count}</span>
                            <span className="text-slate-400">
                              {" "}
                              / {med.totalPrescribed}
                            </span>
                          </td>
                          <td className="py-4">
                            {med.daysUntilRefill <= 0 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                                <AlertOctagon size={12} className="mr-1" />
                                Refill Now
                              </span>
                            ) : med.daysUntilRefill <= 7 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                                <Clock size={12} className="mr-1" />
                                {med.daysUntilRefill} days
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                {med.daysUntilRefill} days
                              </span>
                            )}
                          </td>
                          <td className="py-4">
                            {med.controlled ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                                Controlled
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400">
                                Standard
                              </span>
                            )}
                          </td>
                          <td className="py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => openLogModal(med)}
                                className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
                                title="Log Check"
                              >
                                <CheckCircle size={18} />
                              </button>
                              <button
                                onClick={() => openEditModal(med)}
                                className="p-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => openDetailsModal(med)}
                                className="p-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 rounded-lg transition-colors"
                                title="Details"
                              >
                                <FileText size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="bg-slate-800/50 p-4 rounded-full mb-4">
                    <Pill className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-300 mb-2">
                    No medications found
                  </h3>
                  <p className="text-slate-400 max-w-md mb-6">
                    There are no medications in the system for this resident
                    yet. Click the "Add Medication" button to get started.
                  </p>
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Plus size={18} />
                    Add Medication
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Daily Log Tab */}
          {activeTab === "log" && (
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1 max-w-xs">
                  <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  />
                </div>
                <button
                  onClick={() => setDateFilter("")}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Reset
                </button>
              </div>

              {filteredLogs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-slate-700">
                        <th className="pb-3 text-slate-300 font-medium">
                          Date/Time
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Medication
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Count
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Reason
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Verified By
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Notes
                        </th>
                        <th className="pb-3 text-slate-300 font-medium">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log) => (
                        <tr
                          key={log.id}
                          className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors"
                        >
                          <td className="py-4">
                            <div className="text-white">
                              {moment(log.date).format("MMM DD, YYYY")}
                            </div>
                            <div className="text-slate-400 text-sm">
                              {log.timeCompleted}
                            </div>
                          </td>
                          <td className="py-4 text-slate-300">
                            {log.medicationName}
                          </td>
                          <td className="py-4">
                            <div className="flex items-center">
                              <span className="text-white">
                                {log.actualCount}
                              </span>
                              <span className="text-slate-400">
                                {" "}
                                / {log.expectedCount}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 text-slate-300">
                            {log.reason === "verification" ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                                Verification
                              </span>
                            ) : log.reason === "administration" ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400">
                                Administration
                              </span>
                            ) : log.reason === "refill" ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                Refill
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                                Other
                              </span>
                            )}
                          </td>
                          <td className="py-4">
                            <div className="text-slate-300">
                              Resident: {log.residentName}
                            </div>
                            <div className="text-slate-300">
                              Staff: {log.staffName}
                            </div>
                          </td>
                          <td className="py-4 max-w-xs">
                            <div className="text-slate-300 truncate">
                              {log.notes || "—"}
                            </div>
                          </td>
                          <td className="py-4">
                            <button
                              onClick={() => openLogDetailModal(log)}
                              className="p-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <FileText size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="bg-slate-800/50 p-4 rounded-full mb-4">
                    <Calendar className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-300 mb-2">
                    No logs found
                  </h3>
                  <p className="text-slate-400 max-w-md">
                    {dateFilter
                      ? `No medication logs were found for ${moment(
                          dateFilter
                        ).format("MMMM D, YYYY")}.`
                      : "No medication logs have been recorded yet. Use the 'Log Check' action on a medication to create a log entry."}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "schedule" && (
            <div className="p-6">
              {/* Schedule controls */}
              <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="relative">
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>
                  <button
                    onClick={() =>
                      setScheduleDate(moment().format("YYYY-MM-DD"))
                    }
                    className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Calendar size={16} />
                    Today
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={generateMedicationSchedulePDF}
                    className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <FileText size={16} />
                    Print Schedule
                  </button>
                </div>
              </div>

              {/* Schedule content */}
              <div className="grid grid-cols-1 gap-6">
                {/* Morning schedule */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Sun size={18} className="text-yellow-400" />
                      Morning
                    </h3>
                    <span className="text-slate-400 text-sm">8:00 AM</span>
                  </div>
                  <div className="p-4">
                    {getMedicationsForTimeOfDay("morning").length > 0 ? (
                      <div className="space-y-3">
                        {getMedicationsForTimeOfDay("morning").map((med) => (
                          <div
                            key={med.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                          >
                            <div className="flex justify-between">
                              <div>
                                <div className="text-white font-medium">
                                  {med.name}
                                </div>
                                <div className="text-slate-400 text-sm">
                                  {med.dosage}
                                </div>
                                <div className="text-slate-400 text-sm mt-1">
                                  Take {med.unitsPerDose || 1}{" "}
                                  {med.unitsPerDose > 1 ? "units" : "unit"}
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                {med.controlled && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                                    Controlled
                                  </span>
                                )}
                                <button
                                  onClick={() =>
                                    openLogModal({
                                      ...med,
                                      preFillReason: "administration",
                                    })
                                  }
                                  className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              </div>
                            </div>
                            {med.instructions && (
                              <div className="mt-2 pt-2 border-t border-slate-700 text-slate-400 text-sm">
                                {med.instructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-slate-400">
                          No morning medications scheduled
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Afternoon schedule */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Sun size={18} className="text-amber-400" />
                      Afternoon
                    </h3>
                    <span className="text-slate-400 text-sm">12:00 PM</span>
                  </div>
                  <div className="p-4">
                    {getMedicationsForTimeOfDay("afternoon").length > 0 ? (
                      <div className="space-y-3">
                        {getMedicationsForTimeOfDay("afternoon").map((med) => (
                          <div
                            key={med.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                          >
                            <div className="flex justify-between">
                              <div>
                                <div className="text-white font-medium">
                                  {med.name}
                                </div>
                                <div className="text-slate-400 text-sm">
                                  {med.dosage}
                                </div>
                                <div className="text-slate-400 text-sm mt-1">
                                  Take {med.unitsPerDose || 1}{" "}
                                  {med.unitsPerDose > 1 ? "units" : "unit"}
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                {med.controlled && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                                    Controlled
                                  </span>
                                )}
                                <button
                                  onClick={() =>
                                    openLogModal({
                                      ...med,
                                      preFillReason: "administration",
                                    })
                                  }
                                  className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              </div>
                            </div>
                            {med.instructions && (
                              <div className="mt-2 pt-2 border-t border-slate-700 text-slate-400 text-sm">
                                {med.instructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-slate-400">
                          No afternoon medications scheduled
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Evening schedule */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Moon size={18} className="text-indigo-400" />
                      Evening
                    </h3>
                    <span className="text-slate-400 text-sm">6:00 PM</span>
                  </div>
                  <div className="p-4">
                    {getMedicationsForTimeOfDay("evening").length > 0 ? (
                      <div className="space-y-3">
                        {getMedicationsForTimeOfDay("evening").map((med) => (
                          <div
                            key={med.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                          >
                            <div className="flex justify-between">
                              <div>
                                <div className="text-white font-medium">
                                  {med.name}
                                </div>
                                <div className="text-slate-400 text-sm">
                                  {med.dosage}
                                </div>
                                <div className="text-slate-400 text-sm mt-1">
                                  Take {med.unitsPerDose || 1}{" "}
                                  {med.unitsPerDose > 1 ? "units" : "unit"}
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                {med.controlled && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                                    Controlled
                                  </span>
                                )}
                                <button
                                  onClick={() =>
                                    openLogModal({
                                      ...med,
                                      preFillReason: "administration",
                                    })
                                  }
                                  className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              </div>
                            </div>
                            {med.instructions && (
                              <div className="mt-2 pt-2 border-t border-slate-700 text-slate-400 text-sm">
                                {med.instructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-slate-400">
                          No evening medications scheduled
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bedtime schedule */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Moon size={18} className="text-blue-400" />
                      Bedtime
                    </h3>
                    <span className="text-slate-400 text-sm">10:00 PM</span>
                  </div>
                  <div className="p-4">
                    {getMedicationsForTimeOfDay("bedtime").length > 0 ? (
                      <div className="space-y-3">
                        {getMedicationsForTimeOfDay("bedtime").map((med) => (
                          <div
                            key={med.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                          >
                            <div className="flex justify-between">
                              <div>
                                <div className="text-white font-medium">
                                  {med.name}
                                </div>
                                <div className="text-slate-400 text-sm">
                                  {med.dosage}
                                </div>
                                <div className="text-slate-400 text-sm mt-1">
                                  Take {med.unitsPerDose || 1}{" "}
                                  {med.unitsPerDose > 1 ? "units" : "unit"}
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                {med.controlled && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                                    Controlled
                                  </span>
                                )}
                                <button
                                  onClick={() =>
                                    openLogModal({
                                      ...med,
                                      preFillReason: "administration",
                                    })
                                  }
                                  className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              </div>
                            </div>
                            {med.instructions && (
                              <div className="mt-2 pt-2 border-t border-slate-700 text-slate-400 text-sm">
                                {med.instructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-slate-400">
                          No bedtime medications scheduled
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* As needed (PRN) medications */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Clock size={18} className="text-purple-400" />
                      As Needed (PRN)
                    </h3>
                  </div>
                  <div className="p-4">
                    {getPRNMedications().length > 0 ? (
                      <div className="space-y-3">
                        {getPRNMedications().map((med) => (
                          <div
                            key={med.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                          >
                            <div className="flex justify-between">
                              <div>
                                <div className="text-white font-medium">
                                  {med.name}
                                </div>
                                <div className="text-slate-400 text-sm">
                                  {med.dosage}
                                </div>
                                <div className="text-slate-400 text-sm mt-1">
                                  Take {med.unitsPerDose || 1}{" "}
                                  {med.unitsPerDose > 1 ? "units" : "unit"} as
                                  needed
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                {med.controlled && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                                    Controlled
                                  </span>
                                )}
                                <button
                                  onClick={() =>
                                    openLogModal({
                                      ...med,
                                      preFillReason: "administration",
                                    })
                                  }
                                  className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              </div>
                            </div>
                            {med.instructions && (
                              <div className="mt-2 pt-2 border-t border-slate-700 text-slate-400 text-sm">
                                {med.instructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-slate-400">
                          No PRN medications available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Refill Management Tab */}
          {activeTab === "refill" && (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Needs Refill Now */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700">
                    <h3 className="text-white font-medium">Needs Refill Now</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    {medications.filter((med) => med.daysUntilRefill <= 0)
                      .length > 0 ? (
                      medications
                        .filter((med) => med.daysUntilRefill <= 0)
                        .map((med) => (
                          <div
                            key={med.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <div className="text-white font-medium">
                                  {med.name} {med.dosage}
                                </div>
                              </div>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400">
                                Refill Now
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div className="text-slate-300">
                                Pharmacy: {med.pharmacy}
                              </div>
                              <div className="text-slate-300">
                                Prescriber: {med.prescriber}
                              </div>
                            </div>
                            <button className="w-full px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded transition-colors">
                              Mark as Refilled
                            </button>
                          </div>
                        ))
                    ) : (
                      <div className="text-center py-8">
                        <AlertOctagon className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                        <p className="text-slate-300">
                          No medications need immediate refill
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upcoming Refills */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700">
                    <h3 className="text-white font-medium">
                      Upcoming Refills (Next 7 Days)
                    </h3>
                  </div>
                  <div className="p-4 space-y-3">
                    {medications.filter(
                      (med) =>
                        med.daysUntilRefill > 0 && med.daysUntilRefill <= 7
                    ).length > 0 ? (
                      medications
                        .filter(
                          (med) =>
                            med.daysUntilRefill > 0 && med.daysUntilRefill <= 7
                        )
                        .map((med) => (
                          <div
                            key={med.id}
                            className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <div className="text-white font-medium">
                                  {med.name} {med.dosage}
                                </div>
                                <div className="text-white font-medium">
                                  {med.name} {med.dosage}
                                </div>
                                <div className="text-slate-400 text-sm">
                                  Refill in {med.daysUntilRefill} days
                                </div>
                              </div>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                                Soon
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div className="text-slate-300">
                                Pharmacy: {med.pharmacy}
                              </div>
                              <div className="text-slate-300">
                                Prescriber: {med.prescriber}
                              </div>
                            </div>
                            <button className="w-full px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white text-sm rounded transition-colors">
                              Schedule Refill
                            </button>
                          </div>
                        ))
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                        <p className="text-slate-300">
                          No upcoming refills in the next 7 days
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Medication Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 z-10 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                {isEditMode ? (
                  <>
                    <Edit className="text-blue-400" size={20} />
                    Edit Medication
                  </>
                ) : (
                  <>
                    <Plus className="text-cyan-400" size={20} />
                    Add New Medication
                  </>
                )}
              </h2>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="name"
                  >
                    Medication Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="dosage"
                  >
                    Dosage
                  </label>
                  <input
                    type="text"
                    id="dosage"
                    name="dosage"
                    value={formData.dosage}
                    onChange={handleInputChange}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="totalPrescribed"
                  >
                    Total Pills Prescribed
                  </label>
                  <input
                    type="number"
                    id="totalPrescribed"
                    name="totalPrescribed"
                    value={formData.totalPrescribed}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                </div>

                {/* Replace the current count input field in the Add/Edit Medication modal
   Find this section in your code (around line 800-820) and replace it */}

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="count"
                  >
                    Current Count
                  </label>
                  {isEditMode ? (
                    <>
                      <div className="flex items-center mb-1">
                        <input
                          type="text"
                          value={formData.count}
                          className="w-full bg-slate-800/70 rounded-lg px-4 py-2.5 text-slate-400 border border-slate-700 cursor-not-allowed"
                          disabled
                        />
                      </div>
                      <p className="text-amber-400 text-xs mt-1">
                        <AlertTriangle size={12} className="inline mr-1" />
                        To update medication count, use the Log Check feature
                        for proper documentation
                      </p>
                    </>
                  ) : (
                    <input
                      type="number"
                      id="count"
                      name="count"
                      value={formData.count}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    />
                  )}
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="dosesPerDay"
                  >
                    Doses Per Day
                  </label>
                  <input
                    type="number"
                    id="dosesPerDay"
                    name="dosesPerDay"
                    value={formData.dosesPerDay}
                    onChange={handleInputChange}
                    min="0.25"
                    step="0.25"
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Examples: 1 for once daily, 2 for twice daily, 0.5 for every
                    other day
                  </p>
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="unitsPerDose"
                  >
                    Units Per Dose
                  </label>
                  <input
                    type="number"
                    id="unitsPerDose"
                    name="unitsPerDose"
                    value={formData.unitsPerDose}
                    onChange={handleInputChange}
                    min="0.5"
                    step="0.5"
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    How many pills/tablets/strips should be taken per dose
                  </p>
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="maxDaysSupply"
                  >
                    Max Days Supply
                  </label>
                  <input
                    type="number"
                    id="maxDaysSupply"
                    name="maxDaysSupply"
                    value={formData.maxDaysSupply}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Maximum number of days worth of medication allowed at once
                  </p>
                </div>

                <div className="col-span-2 md:col-span-1">
                  <div className="flex items-center h-full pt-6">
                    <input
                      type="checkbox"
                      id="controlled"
                      name="controlled"
                      checked={formData.controlled}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-cyan-600 rounded border-slate-600 bg-slate-800/50 focus:ring-cyan-500"
                    />
                    <label
                      htmlFor="controlled"
                      className="ml-2 text-slate-300 text-sm font-medium"
                    >
                      Is Controlled Substance?
                    </label>
                  </div>
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="startDate"
                  >
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="endDate"
                  >
                    End Date (Refill Due)
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="prescriber"
                  >
                    Prescriber
                  </label>
                  <input
                    type="text"
                    id="prescriber"
                    name="prescriber"
                    value={formData.prescriber}
                    onChange={handleInputChange}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="pharmacy"
                  >
                    Pharmacy
                  </label>
                  <input
                    type="text"
                    id="pharmacy"
                    name="pharmacy"
                    value={formData.pharmacy}
                    onChange={handleInputChange}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="instructions"
                  >
                    Instructions
                  </label>
                  <textarea
                    id="instructions"
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  ></textarea>
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Administration Schedule
                </label>
                <div className="bg-slate-800/40 p-4 rounded-lg border border-slate-700">
                  <div className="mb-3">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="isPRN"
                        name="isPRN"
                        checked={formData.isPRN}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            isPRN: e.target.checked,
                            // If PRN is checked, clear scheduled times
                            administrationTime: e.target.checked
                              ? []
                              : formData.administrationTime,
                          });
                        }}
                        className="h-4 w-4 text-cyan-600 rounded border-slate-600 bg-slate-800/50 focus:ring-cyan-500"
                      />
                      <label
                        htmlFor="isPRN"
                        className="ml-2 text-slate-300 text-sm"
                      >
                        PRN (As Needed) Medication
                      </label>
                    </div>
                  </div>

                  {!formData.isPRN && (
                    <div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <input
                            type="checkbox"
                            id="time-morning"
                            name="time-morning"
                            checked={
                              Array.isArray(formData.administrationTime) &&
                              formData.administrationTime.includes("morning")
                            }
                            onChange={(e) => {
                              const value = "morning";
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? [...formData.administrationTime, value]
                                    : [value],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? formData.administrationTime.filter(
                                        (time) => time !== value
                                      )
                                    : [],
                                });
                              }
                            }}
                            className="sr-only peer"
                          />
                          <label
                            htmlFor="time-morning"
                            className="flex flex-col items-center justify-center p-3 bg-slate-800/70 border border-slate-700 rounded-lg cursor-pointer peer-checked:border-cyan-500 peer-checked:bg-cyan-500/10 hover:bg-slate-800 transition-colors"
                          >
                            <span className="text-yellow-400 mb-1">☀️</span>
                            <span className="text-slate-300 text-sm">
                              Morning
                            </span>
                            <span className="text-slate-400 text-xs">
                              8:00 AM
                            </span>
                          </label>
                        </div>
                        <div>
                          <input
                            type="checkbox"
                            id="time-afternoon"
                            name="time-afternoon"
                            checked={
                              Array.isArray(formData.administrationTime) &&
                              formData.administrationTime.includes("afternoon")
                            }
                            onChange={(e) => {
                              const value = "afternoon";
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? [...formData.administrationTime, value]
                                    : [value],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? formData.administrationTime.filter(
                                        (time) => time !== value
                                      )
                                    : [],
                                });
                              }
                            }}
                            className="sr-only peer"
                          />
                          <label
                            htmlFor="time-afternoon"
                            className="flex flex-col items-center justify-center p-3 bg-slate-800/70 border border-slate-700 rounded-lg cursor-pointer peer-checked:border-cyan-500 peer-checked:bg-cyan-500/10 hover:bg-slate-800 transition-colors"
                          >
                            <span className="text-amber-400 mb-1">☀️</span>
                            <span className="text-slate-300 text-sm">
                              Afternoon
                            </span>
                            <span className="text-slate-400 text-xs">
                              12:00 PM
                            </span>
                          </label>
                        </div>
                        <div>
                          <input
                            type="checkbox"
                            id="time-evening"
                            name="time-evening"
                            checked={
                              Array.isArray(formData.administrationTime) &&
                              formData.administrationTime.includes("evening")
                            }
                            onChange={(e) => {
                              const value = "evening";
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? [...formData.administrationTime, value]
                                    : [value],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? formData.administrationTime.filter(
                                        (time) => time !== value
                                      )
                                    : [],
                                });
                              }
                            }}
                            className="sr-only peer"
                          />
                          <label
                            htmlFor="time-evening"
                            className="flex flex-col items-center justify-center p-3 bg-slate-800/70 border border-slate-700 rounded-lg cursor-pointer peer-checked:border-cyan-500 peer-checked:bg-cyan-500/10 hover:bg-slate-800 transition-colors"
                          >
                            <span className="text-indigo-400 mb-1">🌙</span>
                            <span className="text-slate-300 text-sm">
                              Evening
                            </span>
                            <span className="text-slate-400 text-xs">
                              6:00 PM
                            </span>
                          </label>
                        </div>
                        <div>
                          <input
                            type="checkbox"
                            id="time-bedtime"
                            name="time-bedtime"
                            checked={
                              Array.isArray(formData.administrationTime) &&
                              formData.administrationTime.includes("bedtime")
                            }
                            onChange={(e) => {
                              const value = "bedtime";
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? [...formData.administrationTime, value]
                                    : [value],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  administrationTime: Array.isArray(
                                    formData.administrationTime
                                  )
                                    ? formData.administrationTime.filter(
                                        (time) => time !== value
                                      )
                                    : [],
                                });
                              }
                            }}
                            className="sr-only peer"
                          />
                          <label
                            htmlFor="time-bedtime"
                            className="flex flex-col items-center justify-center p-3 bg-slate-800/70 border border-slate-700 rounded-lg cursor-pointer peer-checked:border-cyan-500 peer-checked:bg-cyan-500/10 hover:bg-slate-800 transition-colors"
                          >
                            <span className="text-blue-400 mb-1">🌙</span>
                            <span className="text-slate-300 text-sm">
                              Bedtime
                            </span>
                            <span className="text-slate-400 text-xs">
                              10:00 PM
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="mt-3 text-xs text-slate-400">
                        Select all times when this medication should be
                        administered. You can choose multiple times.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={
                    isEditMode ? handleUpdateMedication : handleAddMedication
                  }
                  className={`px-4 py-2 ${
                    isEditMode
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-cyan-600 hover:bg-cyan-700"
                  } text-white rounded-lg transition-colors`}
                >
                  {isEditMode ? "Update Medication" : "Add Medication"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Medication Check Modal */}
      {isLogModalOpen && selectedMedication && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 z-10 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <CheckCircle className="text-cyan-400" size={20} />
                Log Medication Check
              </h2>
              <button
                onClick={() => {
                  setIsLogModalOpen(false);
                  setSelectedMedication(null);
                  setLogData({
                    actualCount: 0,
                    notes: "",
                    reason: "verification",
                  });
                  clearSignatures();
                }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {/* Medication Info Card - Compact */}
              <div className="mb-4 bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-medium text-white">
                      {selectedMedication.name} {selectedMedication.dosage}
                    </h3>
                    <p className="text-slate-300 text-sm">
                      Current count: {selectedMedication.count} of{" "}
                      {selectedMedication.totalPrescribed}
                    </p>
                  </div>
                  {selectedMedication.controlled && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                      Controlled Substance
                    </span>
                  )}
                </div>
              </div>

              {/* Main Form Area - More Efficient Grid */}
              <div className="space-y-4">
                {/* Row 1: Reason + Count + Date (3 columns) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label
                      className="block text-slate-300 text-sm font-medium mb-1"
                      htmlFor="reason"
                    >
                      Reason for Count Change
                    </label>
                    <select
                      id="reason"
                      name="reason"
                      value={logData.reason}
                      onChange={handleLogInputChange}
                      className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    >
                      <option value="verification">
                        Med Count Verification
                      </option>
                      <option value="administration">
                        Medication Administered
                      </option>
                      <option value="refill">Med Refill Received</option>
                      <option value="other">Other (Explain in Notes)</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className="block text-slate-300 text-sm font-medium mb-1"
                      htmlFor="actualCount"
                    >
                      Current Count (Verified)
                    </label>
                    <input
                      type="number"
                      id="actualCount"
                      name="actualCount"
                      value={logData.actualCount}
                      onChange={handleLogInputChange}
                      min="0"
                      className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-1">
                      Date & Time
                    </label>
                    <input
                      type="text"
                      value={moment().format("MMMM DD, YYYY hh:mm A")}
                      className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-slate-400 border border-slate-700 cursor-not-allowed"
                      disabled
                    />
                  </div>
                </div>

                {/* Row 2: Administration-specific fields (Conditional) */}
                {logData.reason === "administration" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-slate-300 text-sm font-medium mb-1"
                        htmlFor="daysSupply"
                      >
                        Days Supply Given
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          id="daysSupply"
                          name="daysSupply"
                          value={logData.daysSupply}
                          onChange={handleLogInputChange}
                          min="1"
                          max={selectedMedication.maxDaysSupply}
                          className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                          required
                        />
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          (max: {selectedMedication.maxDaysSupply})
                        </span>
                      </div>
                    </div>

                    {logData.daysSupply > 0 && (
                      <div>
                        <label className="block text-slate-300 text-sm font-medium mb-1">
                          Expected Count After
                        </label>
                        <input
                          type="text"
                          value={expectedCountAfterDisbursement}
                          className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-slate-400 border border-slate-700 cursor-not-allowed"
                          disabled
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          {selectedMedication.count} - ({logData.daysSupply}{" "}
                          days × {selectedMedication.dosesPerDay} doses/day ×{" "}
                          {selectedMedication.unitsPerDose} units/dose)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes Field */}
                <div>
                  <label
                    className="block text-slate-300 text-sm font-medium mb-1"
                    htmlFor="notes"
                  >
                    Notes{" "}
                    {logData.reason === "other" ||
                    selectedMedication.count !== logData.actualCount
                      ? "(Required)"
                      : "(Optional)"}
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={logData.notes}
                    onChange={handleLogInputChange}
                    rows="2"
                    placeholder="Note any discrepancies or other important information"
                    className="w-full bg-slate-800/50 rounded-lg px-3 py-2 text-white border border-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  ></textarea>
                </div>

                {/* Signatures */}
                <div className="border-t border-slate-700 pt-4">
                  <h3 className="text-slate-300 font-medium mb-3">
                    Signatures
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-300 text-sm font-medium mb-1">
                        Resident Signature
                      </label>
                      <div className="border border-slate-600 rounded-lg overflow-hidden bg-slate-800/30">
                        <SignatureCanvas
                          ref={(ref) => setResidentSignature(ref)}
                          canvasProps={{
                            className: "w-full h-[120px]",
                            willReadFrequently: true,
                          }}
                          backgroundColor="rgba(15, 23, 42, 0)"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-slate-300 text-sm font-medium mb-1">
                        Staff Signature
                      </label>
                      <div className="border border-slate-600 rounded-lg overflow-hidden bg-slate-800/30">
                        <SignatureCanvas
                          ref={(ref) => setStaffSignature(ref)}
                          canvasProps={{
                            className: "w-full h-[120px]",
                            willReadFrequently: true,
                          }}
                          backgroundColor="rgba(15, 23, 42, 0)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between mt-4 pt-3 border-t border-slate-700">
                <button
                  type="button"
                  onClick={clearSignatures}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors text-sm"
                >
                  Clear Signatures
                </button>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsLogModalOpen(false)}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleLogMedication}
                    className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
                  >
                    Submit Log Entry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDetailsModalOpen && selectedMedication && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 z-10 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <FileText className="text-indigo-400" size={20} />
                Medication Check History
              </h2>
              <button
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedMedication(null);
                  setSelectedMedicationLogs([]);
                }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-white">
                      {selectedMedication.name} {selectedMedication.dosage}
                    </h3>
                    <p className="text-slate-300">
                      {selectedMedication.frequency} | Current count:{" "}
                      {selectedMedication.count} of{" "}
                      {selectedMedication.totalPrescribed}
                    </p>
                  </div>
                  {selectedMedication.controlled && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                      Controlled Substance
                    </span>
                  )}
                </div>
              </div>

              {selectedMedicationLogs.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">
                    Check History
                  </h3>
                  <div className="space-y-4">
                    {selectedMedicationLogs.map((log) => (
                      <div
                        key={log.id}
                        className="bg-slate-800/30 border border-slate-700 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="text-white font-medium">
                              {moment(log.date).format("MMMM DD, YYYY")} at{" "}
                              {log.timeCompleted}
                            </div>
                            <div className="text-slate-400 text-sm mt-1">
                              Count: {log.actualCount} of {log.expectedCount}
                              {log.reason && (
                                <span className="ml-2">
                                  Reason:{" "}
                                  {log.reason === "verification"
                                    ? "Count Verification"
                                    : log.reason === "administration"
                                    ? "Medication Administered"
                                    : log.reason === "refill"
                                    ? "Refill Received"
                                    : "Other"}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right text-slate-400 text-sm">
                            Staff: {log.staffName}
                          </div>
                        </div>

                        {log.notes && (
                          <div className="mb-4 text-slate-300 text-sm bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                            <div className="font-medium mb-1">Notes:</div>
                            {log.notes}
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {log.residentSignature && (
                            <div>
                              <div className="text-slate-400 text-sm mb-1">
                                Resident Signature:
                              </div>
                              <div className="border border-slate-600 rounded-lg p-2 bg-slate-800/30">
                                <img
                                  src={log.residentSignature}
                                  alt="Resident Signature"
                                  className="max-h-24 mx-auto"
                                />
                              </div>
                            </div>
                          )}

                          {log.staffSignature && (
                            <div>
                              <div className="text-slate-400 text-sm mb-1">
                                Staff Signature:
                              </div>
                              <div className="border border-slate-600 rounded-lg p-2 bg-slate-800/30">
                                <img
                                  src={log.staffSignature}
                                  alt="Staff Signature"
                                  className="max-h-24 mx-auto"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="bg-slate-800/50 p-4 rounded-full mb-4">
                    <FileText className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-300 mb-2">
                    No check history found
                  </h3>
                  <p className="text-slate-400 max-w-md">
                    There are no medication checks recorded for{" "}
                    {selectedMedication.name} yet. Use the 'Log Check' action to
                    create an entry.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isLogDetailModalOpen && selectedLog && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 z-10 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <FileText className="text-blue-400" size={20} />
                Medication Log Details
              </h2>
              <button
                onClick={() => {
                  setIsLogDetailModalOpen(false);
                  setSelectedLog(null);
                }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {/* Medication info */}
              <div className="mb-6 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <div className="text-lg font-medium text-white mb-1">
                  {selectedLog.medicationName}
                </div>
                <div className="flex justify-between">
                  <div className="flex text-slate-300 text-sm gap-2">
                    <span>
                      {moment(selectedLog.date).format("MMMM DD, YYYY")}
                    </span>
                    <span>at {selectedLog.timeCompleted}</span>
                  </div>
                  {selectedLog.reason && (
                    <div>
                      {selectedLog.reason === "verification" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                          Verification
                        </span>
                      ) : selectedLog.reason === "administration" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400">
                          Administration
                        </span>
                      ) : selectedLog.reason === "refill" ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                          Refill
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                          Other
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Count details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-800/40 p-4 rounded-lg border border-slate-700">
                  <div className="text-slate-400 mb-1 text-sm">
                    Expected Count
                  </div>
                  <div className="text-2xl font-medium text-white">
                    {selectedLog.expectedCount}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedLog.notes && (
                <div className="mb-6">
                  <div className="text-slate-300 text-sm font-medium mb-2">
                    Notes
                  </div>
                  <div className="bg-slate-800/40 p-4 rounded-lg border border-slate-700 text-slate-300">
                    {selectedLog.notes}
                  </div>
                </div>
              )}

              {/* Signatures */}
              <div className="mb-6">
                <div className="text-slate-300 text-sm font-medium mb-2">
                  Verified By
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-800/40 p-4 rounded-lg border border-slate-700">
                    <div className="text-slate-400 mb-2 text-sm">
                      Resident: {selectedLog.residentName}
                    </div>
                    {selectedLog.residentSignature ? (
                      <div className="border border-slate-600 rounded-lg p-2 bg-slate-800/30">
                        <img
                          src={selectedLog.residentSignature}
                          alt="Resident Signature"
                          className="max-h-24 mx-auto"
                        />
                      </div>
                    ) : (
                      <div className="text-slate-500 italic text-sm">
                        No signature provided
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-800/40 p-4 rounded-lg border border-slate-700">
                    <div className="text-slate-400 mb-2 text-sm">
                      Staff: {selectedLog.staffName}
                    </div>
                    {selectedLog.staffSignature ? (
                      <div className="border border-slate-600 rounded-lg p-2 bg-slate-800/30">
                        <img
                          src={selectedLog.staffSignature}
                          alt="Staff Signature"
                          className="max-h-24 mx-auto"
                        />
                      </div>
                    ) : (
                      <div className="text-slate-500 italic text-sm">
                        No signature provided
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationLog;
